import { ContentEnum } from "@/enums/content";
import { getIdNamePairById, showCustomMessage } from "@/methods/utils";
import { useParentAffiliationStore } from "@/stores/parentAffiliationStore";
import { AffiliationStore } from "@/types/affiliation";
import { ContentChildrenInputs } from "@/types/content";
import { IdNamePair } from "@/types/global";
import { defineStore, storeToRefs } from "pinia";
import { useSessionStore } from "./sessionStore";

function getInitialAffiliationStore(): AffiliationStore {
  const initialAffiliationStore: AffiliationStore = {
    CourseBatchTree: null,
    SelectedBatch: null,
    SelectedSubject: null,
    CourseClasses: {},
    CurrentAffiliationId: null,
  };
  return JSON.parse(JSON.stringify(initialAffiliationStore));
}
export const useAffiliationStore = defineStore("affiliationStore", {
  state: (): AffiliationStore => getInitialAffiliationStore(),
  getters: {
    SelectedCourse(): IdNamePair | null {
      if (this.CourseBatchTree === null || this.SelectedBatch === null) {
        return null;
      }
      for (const course of this.CourseBatchTree) {
        for (const batch of course.Batches) {
          if (batch.Id === this.SelectedBatch.Id) {
            return course;
          }
        }
      }
      return null;
    },
  },
  actions: {
    async updateSelectedBatch(batchId: number) {
      const parentAffiliationStore = useParentAffiliationStore();
      const { SelectedAffiliation } = storeToRefs(parentAffiliationStore);
      if (SelectedAffiliation.value === null) {
        return;
      }
      const allBatches = await this.getBatchesForAffiliation(SelectedAffiliation.value.Id);
      const matchedBatch = getIdNamePairById(allBatches, batchId);
      if (!matchedBatch) {
        return;
      }
      this.SelectedBatch = matchedBatch;
    },
    async getCourseBatchTree(affiliationId: number) {
      if (this.CurrentAffiliationId === affiliationId) {
        return;
      }
      this.CurrentAffiliationId = affiliationId;
      this.CourseBatchTree = await useParentAffiliationStore().getCourseBatchTree(affiliationId);
    },
    async getSubjectsForBatch(batchId: number, allSubjectsTitle: string | null) {
      const batchSubjectOptions = await useParentAffiliationStore().getSubjectsForBatch(
        batchId,
        allSubjectsTitle,
      );
      const matchingSubject = batchSubjectOptions.find((s) => s.Id === this.SelectedSubject?.Id);
      if (matchingSubject === undefined) {
        this.SelectedSubject = null;
      }
      return batchSubjectOptions;
    },
    async getAffiliationStatistics(affiliationId: number) {
      return await useParentAffiliationStore().getAffiliationStatistics(affiliationId);
    },
    async getCoursesForAffiliation(affiliationId: number) {
      return await useParentAffiliationStore().getCoursesForAffiliation(affiliationId);
    },
    // TODO: This data should be stored in parentAffiliationStore
    // for each individual affiliation even though affiliations
    // currently can't have access to different classes in the same course
    async getClassesForCourse(affiliationId: number, courseId: number) {
      if (this.CourseClasses[courseId]) {
        return this.CourseClasses[courseId];
      }
      const sessionStore = useSessionStore();
      const { allApiVerified } = storeToRefs(sessionStore);
      const contentInputs: ContentChildrenInputs = {
        selectedContent: {
          CourseId: courseId,
          ClassId: null,
          SubjectId: null,
          TopicId: null,
          CourseChapterId: null,
          KSCClusterId: null,
          KSCId: null,
        },
        userId: 0,
        isStudent: false,
        onlyActive: true,
        skipTopics: true,
        affiliationId: affiliationId,
        parentContentType: ContentEnum.COURSE,
      };
      const response = await allApiVerified.value.contentApi.getContentChildren(contentInputs);
      if (!response.isSuccess) {
        showCustomMessage({
          messageText: "An error occured getting list of classes for course.",
          messageType: "error",
        });
        return [];
      }
      this.CourseClasses[courseId] = response.data;
      return this.CourseClasses[courseId];
    },
    async getBatchesForAffiliationCourse(affiliationId: number, courseId: number) {
      return await useParentAffiliationStore().getBatchesForAffiliationCourse(
        affiliationId,
        courseId,
      );
    },
    async getBatchesForAffiliation(affiliationId: number) {
      return await useParentAffiliationStore().getBatchesForAffiliationCourse(affiliationId, null);
    },
  },
});
